import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SuspendedLoadings.translations.json';
import React, { FC, useMemo } from 'react';
import { DashboardPanel } from '../DashboardPanel';
import { useGetSuspendedLoadingsQuery } from './query/useGetSuspendedLoadingsQuery';
import { keepPreviousData } from '@tanstack/react-query';
import { Flex } from 'antd';
import { InfoIconUrl, Tooltip } from '../../common/components';
import { SuspendedLoadingsDto } from './schema/types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Table } from '../../common/tanstacktable';
import { formatDateTimeShort } from '../../common/utils';

const { addTranslations, t } = i18nNsFactory('suspended-loadings');
addTranslations(translations);

export const SuspendedLoadings = () => {
  const { data: suspendedLoadings = [], isLoading } =
    useGetSuspendedLoadingsQuery({
      placeholderData: keepPreviousData,
    });

  return (
    <DashboardPanel
      title={t('title')}
      infoIconUrl={InfoIconUrl.SUSPENDED_LOADINGS}
      isLoading={isLoading}
      isEmpty={!suspendedLoadings.length}
      modalContent={<SuspendedLoadingsModalContent items={suspendedLoadings} />}
    >
      <Flex gap="small" vertical>
        {suspendedLoadings.map((suspendedLoading) => {
          return (
            <Flex key={suspendedLoading.id}>
              <Tooltip
                title={<SuspendedLoadingsTooltip item={suspendedLoading} />}
              >
                {suspendedLoading.name}
              </Tooltip>
            </Flex>
          );
        })}
      </Flex>
    </DashboardPanel>
  );
};

export const SuspendedLoadingsTooltip: FC<{
  item: SuspendedLoadingsDto;
}> = ({ item }) => {
  return (
    <Flex vertical>
      <strong>{item.name}</strong>
      <Flex gap="small">
        <div>{t('autoLoadingPaused.label')}:</div>
        <div>{formatDateTimeShort(item.loadingPausedAt)}</div>
        <div>({item.loadingPausedBy})</div>
      </Flex>
    </Flex>
  );
};

const columnHelper = createColumnHelper<SuspendedLoadingsDto>();
export const SuspendedLoadingsModalContent: FC<{
  items: SuspendedLoadingsDto[];
}> = ({ items }) => {
  const columns = useMemo(() => {
    const columns: ColumnDef<SuspendedLoadingsDto, any>[] = [
      columnHelper.accessor('name', {
        header: t('name.label'),
      }),
      columnHelper.accessor('loadingPausedAt', {
        header: t('loadingPausedAt.label'),
        cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
      }),
      columnHelper.accessor('loadingPausedBy', {
        header: t('loadingPausedBy.label'),
      }),
    ];

    return columns;
  }, []);

  return (
    <Table
      name="waiting-statuses-table"
      columns={columns}
      data={items}
      hideClearFilters
      columnOrderingDisabled={true}
      columnResizeDisabled={true}
    />
  );
};
